import { accountingType } from '@/features/master-table/models';
import { booleanStringType } from '@/utils';
import {
  fundSheetTypeEnum,
  implementationMethodEnum,
  projectCategoryEnum,
  reviewTypeEnum,
  sheetTypeEnum,
} from '@/utils/enum';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsJson,
  parseAsString,
  parseAsStringLiteral,
} from 'nuqs';
import { useFormContext, useWatch } from 'react-hook-form';
import type { AnalysisSearchFormType } from '../_models/analysis-search';
import type { ProjectSearchFormType } from '../_models/projects-search';
import {
  AmountSearchConditionSchema,
  candidateOfInspectionByExternalExpert,
  initialValueOfProjectSearchForm,
} from '../_models/projects-search';
import { useProjectsSearchStore } from '../_stores/projects-search-store';
import {
  indicatorDataNatureTypeEnum,
  indicatorTermEnum,
  indicatorTypeEnum,
} from '../_utils/enum';

export const projectListSearchNuqs = (currentFiscalYear: number) => {
  const initialValue = initialValueOfProjectSearchForm(currentFiscalYear);
  return {
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(30),
    fiscalYear: parseAsString.withDefault(initialValue.fiscalYear.toString()),
    sheetType: parseAsStringLiteral(sheetTypeEnum.options).withDefault('RS'),
    fundSheetType: parseAsStringLiteral(fundSheetTypeEnum.options),
    q: parseAsString,
    // テキスト検索対象
    isSearchTargetProjectName: parseAsBoolean.withDefault(false),
    cascader: parseAsString,
    corporateNumber: parseAsString,
    categories: parseAsArrayOf(
      parseAsStringLiteral(projectCategoryEnum.options),
    ),
    implementationMethods: parseAsArrayOf(
      parseAsStringLiteral(implementationMethodEnum.options),
    ),
    accountingType: parseAsStringLiteral(accountingType.options),
    accountingTypeIds: parseAsArrayOf(parseAsString),
    skipAdministrativeReformTeamEvaluation: parseAsStringLiteral([
      ...booleanStringType.options,
      'all',
    ]).withDefault(initialValue.skipAdministrativeReformTeamEvaluation),
    projectNumbers: parseAsString,
    isPublished: parseAsStringLiteral(booleanStringType.options),
    inspectionByExternalExpertReviewType: parseAsArrayOf(
      parseAsStringLiteral(reviewTypeEnum.options),
    ),
    isCandidateOfInspectionByExternalExpert: parseAsStringLiteral(
      candidateOfInspectionByExternalExpert.options,
    ),
    ordering: parseAsString,
    // 事業年度（作成年度）
    startYear: parseAsInteger,
    endYear: parseAsInteger,
    // 点検最終実施年度
    lastImplementedFiscalYearRangeMax: parseAsInteger,
    lastImplementedFiscalYearRangeMin: parseAsInteger,
    // PS用
    organizationIds: parseAsArrayOf(parseAsString),
    otherResponsibleOrganizationIds: parseAsArrayOf(parseAsString),
    policyIds: parseAsArrayOf(parseAsString),
    expenditureBudgetPurposeIds: parseAsArrayOf(parseAsString),
    // 複数数式条件用
    formulaConditions: parseAsJson(AmountSearchConditionSchema.parse),
    // 効果発現経路（指標）検索
    indicatorKeyword: parseAsString,
    indicatorKeywordSearchOnlyTitleAndDescription:
      parseAsBoolean.withDefault(false),
    indicatorType: parseAsStringLiteral(indicatorTypeEnum.options),
    indicatorTerm: parseAsStringLiteral(indicatorTermEnum.options),
    indicatorDataNatureType: parseAsStringLiteral(
      indicatorDataNatureTypeEnum.options,
    ),
    hasOnlyQualitativeIndicator: parseAsBoolean.withDefault(false),
  };
};

/**
 * 検索タイプに応じて参照すべき年度を取得するカスタムフック
 *
 * @returns {number | undefined}
 * - 分析画面の場合: 選択された年度範囲の最大値
 * - 事業一覧の場合: 選択された年度
 * - 未選択の場合: undefined
 *
 * @example
 * const referenceFiscalYear = useGetReferenceFiscalYearBySearchType();
 * // 分析画面で 2023-2025 が選択されている場合 → 2025
 * // 事業一覧で 2024 が選択されている場合 → 2024
 */
export const useGetReferenceFiscalYearBySearchType = (): number | undefined => {
  const { type } = useProjectsSearchStore();
  const { control: projectSearchControl } =
    useFormContext<ProjectSearchFormType>();

  const fiscalYear = useWatch({
    control: projectSearchControl,
    name: 'fiscalYear',
  });
  const { control: analysisSearchControl } =
    useFormContext<AnalysisSearchFormType>();

  const fiscalYearRange = useWatch({
    control: analysisSearchControl,
    name: 'fiscalYearRange',
  });
  const maxFiscalYearByAnalysisSearch = fiscalYearRange
    ? Math.max(
        fiscalYearRange.at(0)?.year() ?? 0,
        fiscalYearRange.at(1)?.year() ?? 0,
      )
    : undefined;
  return type === 'analysis'
    ? maxFiscalYearByAnalysisSearch
    : fiscalYear?.year();
};
